import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2Off } from 'lucide-react';
import { Meta } from '@/components/common';
import { PatientWorkspace } from '@/components/features/patient/PatientWorkspace';
import { LinkUnavailable } from '@/components/features/review/LinkUnavailable';
import { layersOf, useDesignMetadata, type DesignMetadata } from '@/lib/live-design';
import { ApiError } from '@/services/api/client';
import { reviewService } from '@/services/api/review.service';
import { queryKeys } from '@/services/queries/keys';
import { Logo } from '@/shared/ui/logo';
import { Spinner } from '@/shared/ui/spinner';
import type { PatientOpenResponse, PatientShare, SceneManifest } from '@/types';

/**
 * The patient's view-only link: their crown in 3D, in their language, with
 * their dentist's message and the proposed fitting date, which they confirm or
 * ask to move. The token in the path is a patient token: it reaches this design
 * read-only and nothing else.
 */

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-background px-6 text-center">
      <Logo />
      {children}
    </div>
  );
}

/**
 * The share, with the design's own shade filling any tooth the link left
 * without one - the crown is painted in it, and the page names it.
 */
function shareOf(view: PatientOpenResponse, metadata: DesignMetadata | undefined): PatientShare {
  const shade = metadata?.shade || null;
  const teeth =
    view.teeth.length > 0
      ? view.teeth.map((tooth) => ({
          ...tooth,
          shade:
            tooth.shade ??
            (tooth.tooth === metadata?.tooth || view.teeth.length === 1 ? shade : null),
        }))
      : metadata?.tooth
        ? [{ tooth: metadata.tooth, shade }]
        : [];
  return {
    language: view.language,
    message: view.message,
    teeth,
    appointment: view.appointment,
  };
}

export function PatientPage() {
  const { token = '' } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const key = queryKeys.patient.open(token);

  const open = useQuery({
    queryKey: key,
    enabled: token.length > 0,
    retry: false,
    queryFn: ({ signal }) => reviewService.openPatient(token, signal),
  });
  const metadata = useDesignMetadata(key, open.data?.metadataUrl);
  const answer = useMutation({
    mutationFn: (status: 'confirmed' | 'reschedule_requested') =>
      reviewService.answerPatient(token, { status }),
    onSuccess: (view) => {
      queryClient.setQueryData(key, view);
    },
  });

  const manifest = useMemo<SceneManifest | null>(
    () =>
      open.data && !metadata.isPending
        ? {
            source: { type: 'glb', url: open.data.glbUrl },
            layers: layersOf(metadata.data),
            margins: [],
          }
        : null,
    [open.data, metadata.isPending, metadata.data],
  );
  const share = useMemo(
    () => (open.data ? shareOf(open.data, metadata.data) : null),
    [open.data, metadata.data],
  );

  const error = open.error instanceof ApiError ? open.error : null;
  if (error?.code === 'patient_link_closed') {
    return (
      <Centered>
        <Link2Off className="size-6 text-muted-foreground" />
        <p className="max-w-sm text-sm text-muted-foreground">
          Your dentist has closed this link. Ask the clinic if you would like to see your crown
          again.
        </p>
      </Centered>
    );
  }
  if (error?.isDead) return <LinkUnavailable code={error.code} />;

  if (open.isPending || (open.data && !manifest)) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }

  if (open.isError || !open.data || !manifest || !share) {
    return (
      <Centered>
        <p className="max-w-sm text-sm text-muted-foreground">
          {error?.message ?? 'Something went wrong opening this page. Try reloading it.'}
        </p>
      </Centered>
    );
  }

  return (
    <>
      <Meta title="Your new crown" />
      <PatientWorkspace
        manifest={manifest}
        share={share}
        onAppointment={async (status) => {
          await answer.mutateAsync(status);
        }}
      />
    </>
  );
}
